import { useCallback, useEffect, useMemo, useState } from "react";

import { Modal, App, Button, Flex, Grid, Tooltip } from "antd";

import ObjectsTable from "@/components/tables/ObjectsTable";
import TableTaskModal from "@/components/modals/TableTaskModal";
import TableTasksSyncResultModal from "@/components/modals/TableTasksSyncResultModal";
import ContextMenu from "@/components/containers/ContextMenu";

import ClipboardCopyIcon from "@/icons/ClipboardCopyIcon";
import RefreshIcon from "@/icons/RefreshIcon";

import { getObjects, deleteObject, getObjectTasks } from "@/api/objects";
import { syncItemsFromTable } from "@/api/tasks";
import { getPluralizedWord } from "@/utils";

const normalizeSorter = (sorter) => {
  const sortArray = Array.isArray(sorter) ? sorter : [sorter];

  return sortArray
    .filter((item) => item?.order)
    .map((item) => ({
      columnKey: item.columnKey,
      field: item.field,
      order: item.order,
    }));
};
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const { useBreakpoint } = Grid;

const TableField = ({
  title,
  value,
  object_uuid,
  object_name,
  definition_uuid,
  isAdmins,
  onRefresh,
  onRelatedTasksRefresh,
  unit_uuid,
  object_unit_uuid,
  parent_field,
  isEditable = false,
  canEditObject = false,
  units,
}) => {
  const { message } = App.useApp();
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  const initialTableParams = {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
    sort: [
      {
        columnKey: "created_at",
        field: "created_at",
        order: "descend",
      },
    ],
  };
  const [tableParams, setTableParams] = useState(initialTableParams);

  const defInfo = value?.definition;
  const relation_field_name = value?.relation_field_name;

  const [data, setData] = useState(value?.rows || []);
  const [pending, setPending] = useState(false);
  const [relatedTasksByObject, setRelatedTasksByObject] = useState({});
  const [relatedTasksLoadingByObject, setRelatedTasksLoadingByObject] =
    useState({});
  const [showDeletedTasks, setShowDeletedTasks] = useState(false);
  const [selectedRowUuids, setSelectedRowUuids] = useState([]);
  const [selectedRowsByUuid, setSelectedRowsByUuid] = useState({});
  const [syncingTasks, setSyncingTasks] = useState(false);
  const [syncingTaskUuids, setSyncingTaskUuids] = useState([]);
  const [syncResult, setSyncResult] = useState(null);

  // Модальное окно создания задач из таблицы ресурсов
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const filteredFields = useMemo(() => {
    return (value?.tableForm?.fields || []).filter((field) => {
      return !(field?.object && field.object === definition_uuid);
    });
  }, [value?.tableForm?.fields, definition_uuid]);

  const isDateOnlyDueDateField = useMemo(() => {
    const dueDateFieldUuid =
      parent_field?.due_date_field_uuid ||
      parent_field?.due_date_field?.uuid ||
      (typeof parent_field?.due_date_field === "string"
        ? parent_field.due_date_field
        : null);
    const dueDateField = (value?.tableForm?.fields || []).find(
      (field) =>
        field.uuid === dueDateFieldUuid || field.field === dueDateFieldUuid,
    );

    return (
      (parent_field?.due_date_field?.type || dueDateField?.type) === "date"
    );
  }, [parent_field, value?.tableForm?.fields]);

  // Закомментировано по просьбе клиента не удалять
  //   const dateFields = useMemo(() => {
  //   return filteredFields.filter(
  //     (field) => field?.type === "moment" || field?.type === "date",
  //   );
  // }, [filteredFields]);

  useEffect(() => {
    const params = {
      ...initialTableParams,
      pagination: {
        ...initialTableParams.pagination,
        total: value?.total || 0,
        pageSize: value?.perPage || 10,
        current: value?.page || 1,
      },
    };
    setTableParams(params);
    fetchData(params);

    // eslint-disable-next-line
  }, []);

  const fetchData = useCallback(
    async (tParams, shouldShowDeletedTasks = showDeletedTasks) => {
      setPending(true);
      window.__MAP_SCREENSHOT_RENDERER__?.reset();

      const currentParams = tParams || tableParams;

      const sortString = currentParams.sort
        .map(({ columnKey, order }) => {
          return order === "descend" ? `-${columnKey}` : columnKey;
        })
        .join(",");

      const params = {
        page: currentParams.pagination.current,
        perpage: currentParams.pagination.pageSize,
        sort: sortString,
        object_type: "all",
        definition_uuid: defInfo?.uuid,
        include_has_related_tasks: true,
        include_deleted_tasks_for_related_tasks: shouldShowDeletedTasks,
        [relation_field_name]: [object_uuid],
      };

      try {
        const { objects, total, page, perPage } = await getObjects(
          params,
          isAdmins,
        );
        setData(objects);
        setRelatedTasksByObject({});
        setRelatedTasksLoadingByObject({});

        const updatedTableParams = {
          ...currentParams,
          pagination: {
            ...currentParams.pagination,
            total,
            current: page,
            pageSize: perPage,
          },
        };
        setTableParams(updatedTableParams);
      } catch (error) {
        message.open({
          type: "error",
          content: error.message,
          duration: 10,
        });
      } finally {
        setPending(false);
      }
    },
    [
      defInfo?.uuid,
      isAdmins,
      message,
      object_uuid,
      relation_field_name,
      showDeletedTasks,
      tableParams,
    ],
  );

  const handleTableChange = async (pagination, filters, sorter) => {
    const updated = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
      sort: normalizeSorter(sorter),
    };

    setTableParams(updated);
    fetchData(updated);
  };

  // Загружаем связанные задачи строки при первом раскрытии
  const handleLoadRelatedTasks = useCallback(
    async (record) => {
      if (!record?.uuid || relatedTasksByObject[record.uuid]) return;

      setRelatedTasksLoadingByObject((prev) => ({
        ...prev,
        [record.uuid]: true,
      }));

      try {
        const tasks = await getObjectTasks(
          {
            uuid: record.uuid,
            definition_uuid: defInfo?.uuid,
            parent_object_uuid: object_uuid,
            field_uuid: parent_field?.field,
            include_deleted: showDeletedTasks,
          },
          isAdmins,
        );

        setRelatedTasksByObject((prev) => ({
          ...prev,
          [record.uuid]: tasks || [],
        }));
      } catch (error) {
        message.open({
          type: "error",
          content: error.message,
          duration: 10,
        });

        setRelatedTasksByObject((prev) => ({
          ...prev,
          [record.uuid]: [],
        }));
      } finally {
        setRelatedTasksLoadingByObject((prev) => ({
          ...prev,
          [record.uuid]: false,
        }));
      }
    },
    [
      defInfo?.uuid,
      isAdmins,
      message,
      object_uuid,
      parent_field?.field,
      relatedTasksByObject,
      showDeletedTasks,
    ],
  );

  const selectedRows = useMemo(
    () =>
      selectedRowUuids
        .map(
          (uuid) =>
            selectedRowsByUuid[uuid] || data.find((item) => item.uuid === uuid),
        )
        .filter(Boolean),
    [data, selectedRowUuids, selectedRowsByUuid],
  );

  const selectedTasks = useMemo(
    () => selectedRows.flatMap((row) => relatedTasksByObject[row.uuid] || []),
    [relatedTasksByObject, selectedRows],
  );

  const selectedTableTasks = useMemo(
    () =>
      Array.from(
        new Map(
          selectedTasks
            .filter((task) => task?.uuid && task.created_from_table === true)
            .map((task) => [task.uuid, task]),
        ).values(),
      ),
    [selectedTasks],
  );

  const selectedRowsWithoutTableTasks = useMemo(
    () =>
      selectedRows.filter(
        (row) =>
          !(relatedTasksByObject[row.uuid] || []).some(
            (task) => task?.created_from_table === true,
          ),
      ),
    [relatedTasksByObject, selectedRows],
  );

  const selectedRowsForTaskCreation = useMemo(
    () =>
      selectedRows.filter((row) => {
        if (defInfo?.use_permission_rules || !object_unit_uuid) {
          return true;
        }

        const rowUnitUuid =
          row?.selfObject?.unit_uuid || row?.unit?.uuid || null;

        return rowUnitUuid === object_unit_uuid;
      }),
    [defInfo?.use_permission_rules, object_unit_uuid, selectedRows],
  );

  const isSelectedTasksCountLoading = selectedRows.some(
    (row) => row?.has_related_tasks && relatedTasksLoadingByObject[row.uuid],
  );

  useEffect(() => {
    if (!selectedRows.length) return;

    selectedRows.forEach((row) => {
      if (
        row?.has_related_tasks &&
        !relatedTasksByObject[row.uuid] &&
        !relatedTasksLoadingByObject[row.uuid]
      ) {
        handleLoadRelatedTasks(row);
      }
    });
  }, [
    handleLoadRelatedTasks,
    relatedTasksByObject,
    relatedTasksLoadingByObject,
    selectedRows,
  ]);

  const handleRowsSelect = useCallback(
    (nextSelectedRowUuids, nextRows = []) => {
      setSelectedRowUuids(nextSelectedRowUuids);
      setSelectedRowsByUuid((prev) => {
        const selectedSet = new Set(nextSelectedRowUuids);
        const next = Object.fromEntries(
          Object.entries(prev).filter(([uuid]) => selectedSet.has(uuid)),
        );

        nextRows.forEach((row) => {
          if (row?.uuid && selectedSet.has(row.uuid)) {
            next[row.uuid] = row;
          }
        });

        return next;
      });
    },
    [],
  );

  const handleSyncResultClose = useCallback(() => setSyncResult(null), []);

  const buildSyncPayload = useCallback(
    (taskUuids) => ({
      object_uuid,
      definition_uuid,
      field_uuid: parent_field?.field,
      task_uuids: taskUuids,
      ...(isDateOnlyDueDateField ? { time_zone: userTimeZone } : {}),
    }),
    [definition_uuid, isDateOnlyDueDateField, object_uuid, parent_field?.field],
  );

  const handleSyncSelectedTasks = useCallback(async () => {
    if (isSelectedTasksCountLoading || syncingTasks) return;

    if (!selectedTableTasks.length) {
      message.warning(
        "В выбранных строках нет задач для обновления. Для создания задач воспользуйтесь «Создать задачи».",
      );
      return;
    }

    if (selectedRowsWithoutTableTasks.length) {
      message.warning(
        "Не для всех выбранных строк найдены задачи для обновления. Для создания задач воспользуйтесь «Создать задачи».",
        8,
      );
    }

    setSyncingTasks(true);
    try {
      const result = await syncItemsFromTable(
        buildSyncPayload(selectedTableTasks.map((task) => task.uuid)),
      );

      setSyncResult({ result, requestedTasks: selectedTableTasks });
      handleRowsSelect([]);
      await Promise.all([fetchData(), onRelatedTasksRefresh?.()]);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setSyncingTasks(false);
    }
  }, [
    buildSyncPayload,
    fetchData,
    handleRowsSelect,
    message,
    onRelatedTasksRefresh,
    isSelectedTasksCountLoading,
    selectedRowsWithoutTableTasks.length,
    selectedTableTasks,
    syncingTasks,
  ]);

  const handleSyncTableTask = useCallback(
    async (row, task) => {
      if (!row?.uuid || !task?.uuid) return;

      setSyncingTaskUuids((prev) => [...prev, task.uuid]);
      try {
        const result = await syncItemsFromTable(buildSyncPayload([task.uuid]));

        setSyncResult({ result, requestedTasks: [task] });
        const tasks = await getObjectTasks(
          {
            uuid: row.uuid,
            definition_uuid: defInfo?.uuid,
            parent_object_uuid: object_uuid,
            field_uuid: parent_field?.field,
          },
          isAdmins,
        );
        setRelatedTasksByObject((prev) => ({
          ...prev,
          [row.uuid]: tasks || [],
        }));
        await onRelatedTasksRefresh?.();
      } catch (error) {
        message.open({
          type: "error",
          content: error.message,
          duration: 10,
        });
      } finally {
        setSyncingTaskUuids((prev) =>
          prev.filter((uuid) => uuid !== task.uuid),
        );
      }
    },
    [
      buildSyncPayload,
      defInfo?.uuid,
      isAdmins,
      message,
      object_uuid,
      onRelatedTasksRefresh,
      parent_field?.field,
    ],
  );

  const handleShowDeletedTasksChange = async (checked) => {
    setShowDeletedTasks(checked);
    await fetchData(undefined, checked);
  };

  // Обработчик удаления
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить ${item.name}?`,
      content: "Это действие нельзя будет отменить. Данные будут удалены.",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем объект после подтверждения
  const handleConfirmDelete = async (item) => {
    setPending(true);
    try {
      await deleteObject({ uuid: item.uuid, definition_uuid: defInfo?.uuid });
      onRefresh?.();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setPending(false);
    }
  };

  // Обработчик добавления задачи. Открывает модальное окно
  const handleAddTask = () => {
    if (!selectedRowsForTaskCreation.length) {
      message.warning(
        "Для выбранных строк нельзя создать задачи в текущей рабочей группе.",
      );
      return;
    }

    if (selectedRowsForTaskCreation.length !== selectedRows.length) {
      message.warning(
        "Строки из другой рабочей группы не будут дополнены задачами.",
        8,
      );
    }

    setCurrentItem({
      object_uuid: object_uuid,
      definition_uuid: definition_uuid,
      field_uuid: parent_field?.field,
      unit_uuid: object_unit_uuid,
      definition_name: defInfo?.name,
      object_name,
      row_uuids: selectedRowsForTaskCreation.map((row) => row.uuid),
    });
    setIsTaskModalOpen(true);
  };

  // Обработчик сохранения задачи
  const handleSaveTask = async () => {
    setIsTaskModalOpen(false);
    setCurrentItem(null);
    handleRowsSelect([]);
    await Promise.all([fetchData(), onRelatedTasksRefresh?.()]);
  };

  // Обработчик закрытия задачи
  const handleCancelTask = () => {
    setIsTaskModalOpen(false);
    setCurrentItem(null);
  };

  const handleUpdateObjectLocally = (uuid, value) => {
    setData((prev) =>
      prev.map((obj) => (obj.uuid === uuid ? { ...obj, ...value } : obj)),
    );
  };

  const isCreateTasksDisabled = (isCreateMode) =>
    isCreateMode || !selectedRowUuids.length;
  const isUpdateTasksDisabled = (isCreateMode) =>
    isCreateMode ||
    isSelectedTasksCountLoading ||
    syncingTasks ||
    !selectedRowUuids.length;

  return (
    <>
      <Flex vertical>
        <ObjectsTable
          title={title}
          sortedInfo={tableParams.sort}
          objects={data}
          fields={filteredFields}
          loading={pending}
          pagination={tableParams.pagination}
          onChange={handleTableChange}
          showUnit={defInfo?.type !== "repository"}
          unit_uuid={unit_uuid}
          isAdmins={isAdmins}
          showSelect={canEditObject}
          selectedRowKeys={selectedRowUuids}
          onRowsSelect={handleRowsSelect}
          resize
          definitionId={defInfo?.uuid}
          color={defInfo?.color}
          onDeleteItem={handleDeleteItem}
          onRefresh={() => {
            fetchData();
          }}
          isNspdEnabled={!!defInfo?.is_nspd_enabled}
          units={units}
          canBeEdited={defInfo?.type === "object" && isEditable}
          showEditActions={isEditable}
          showCreateButton={isEditable}
          createDraftConfig={{
            definitionUuid: defInfo?.uuid,
            relationFieldName: relation_field_name,
            parentObjectUuid: object_uuid,
            objectUnitUuid: object_unit_uuid,
          }}
          footerActions={({ isCreateMode }) => (
            <Flex align="center" gap={10} style={{ width: "100%" }}>
              {canEditObject && (
                <>
                  {selectedRowUuids.length > 0 && (
                    <div>
                      {`Выделено ${selectedRowUuids.length} ${getPluralizedWord(
                        selectedRowUuids.length,
                        ["строка", "строки", "строк"],
                      )}`}
                      {`, ${selectedTableTasks.length} ${getPluralizedWord(
                        selectedTableTasks.length,
                        ["задача", "задачи", "задач"],
                      )} для обновления`}
                      {isSelectedTasksCountLoading ? " (подсчет...)" : ""}
                    </div>
                  )}
                  {isMobile ? (
                    <div style={{ marginLeft: "auto" }}>
                      <ContextMenu
                        items={[
                          {
                            key: "create_tasks",
                            disabled: isCreateTasksDisabled(isCreateMode),
                            onClick: handleAddTask,
                            label: (
                              <Flex align="center" gap={10}>
                                <ClipboardCopyIcon
                                  style={{ fontSize: 16, color: "#e66a25" }}
                                />
                                Создать задачи
                              </Flex>
                            ),
                          },
                          {
                            key: "update_tasks",
                            disabled: isUpdateTasksDisabled(isCreateMode),
                            onClick: handleSyncSelectedTasks,
                            label: (
                              <Flex align="center" gap={10}>
                                <RefreshIcon
                                  style={{ fontSize: 16, color: "#e66a25" }}
                                />
                                Обновить задачи
                              </Flex>
                            ),
                          },
                        ]}
                      />
                    </div>
                  ) : (
                    <>
                      <Tooltip
                        title={
                          selectedRowUuids.length
                            ? null
                            : "Отметьте строки для создания задач"
                        }
                      >
                        <span>
                          <Button
                            icon={<ClipboardCopyIcon />}
                            disabled={isCreateTasksDisabled(isCreateMode)}
                            onClick={handleAddTask}
                          >
                            Создать задачи
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          !selectedRowUuids.length
                            ? "Отметьте строки для обновления задач"
                            : null
                        }
                      >
                        <span>
                          <Button
                            icon={<RefreshIcon />}
                            loading={syncingTasks}
                            disabled={isUpdateTasksDisabled(isCreateMode)}
                            onClick={handleSyncSelectedTasks}
                          >
                            Обновить задачи
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </Flex>
          )}
          onLocalUpdate={handleUpdateObjectLocally}
          relatedTasksByObject={relatedTasksByObject}
          relatedTasksLoadingByObject={relatedTasksLoadingByObject}
          onLoadRelatedTasks={handleLoadRelatedTasks}
          onSyncTableTask={handleSyncTableTask}
          syncingTaskUuids={syncingTaskUuids}
          showDeletedTasks={showDeletedTasks}
          onShowDeletedTasksChange={handleShowDeletedTasksChange}
          showDeletedTasksPending={
            pending ||
            Object.values(relatedTasksLoadingByObject).some(Boolean)
          }
        />
      </Flex>

      {isTaskModalOpen && (
        <TableTaskModal
          isAdmins={isAdmins}
          data={currentItem}
          //dateFields={dateFields}
          selectedRowUuids={selectedRowsForTaskCreation.map((row) => row.uuid)}
          visible={isTaskModalOpen}
          onSave={handleSaveTask}
          onCancel={handleCancelTask}
          canEditUnit={
            defInfo?.type === "object" ? defInfo?.use_permission_rules : true
          }
        />
      )}

      <TableTasksSyncResultModal
        visible={!!syncResult}
        result={syncResult?.result}
        requestedTasks={syncResult?.requestedTasks}
        onCancel={handleSyncResultClose}
      />
    </>
  );
};

export default TableField;
